import { useState, useEffect } from "react"
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js"
import { motion } from "framer-motion"
import { ErrorMessage, useField, useFormikContext } from "formik"
import * as Yup from "yup"
import Moment from "moment-timezone"
import { extendMoment } from "moment-range"
const moment = extendMoment(Moment)

import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import classNames from "classnames"
import axios from "axios"

import { API } from "utils/api"
import { mapInClinicTreatment, trackConsultationSubmit } from "utils/tracking"
import { useAnimation } from "utils/useAnimation"

import { WysiwygText } from "components/shared"
import { Field, Form, Select, Checkbox } from "components/form"
import Link from "next/link"
import { useRouter } from "next/router"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import * as Sentry from "@sentry/nextjs"
import { PhoneNumberField } from "./OnlineConsultationForm"

dayjs.extend(advancedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

const getStripeCustomerId = (patient) =>
  patient?.custom_fields?.sections
    ?.find((section) => section.name === "Payments")
    ?.fields?.find((field) => field.name === "stripe_customer_id")?.value

const getPaymentErrorMessage = (error) => {
  const message = error?.response?.data?.error?.message || error?.message || ""

  if (message.includes("client_secret") || message.includes("intent secret")) {
    return "We couldn't start your payment. Please try again, or contact us and we'll help you book."
  }

  if (message.includes("Stripe not initialized") || message.includes("still loading")) {
    return "Payment form is still loading. Please wait a moment and try again."
  }

  if (message.includes("Stripe customer")) {
    return "We couldn't set up your payment details. Please try again, or contact us and we'll help you book."
  }

  if (message) {
    return message
  }

  return "Something went wrong while booking. Please contact us and we'll resolve it."
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#2F4B73",
      fontSmoothing: "antialiased",
      fontSize: "17px",
      "::placeholder": {
        // color: "#9EA6B2",
        color: "#9ca3b0",
        fontWeight: 500,
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
}

const CardField = () => {
  const { variants } = useAnimation()
  return (
    <motion.div className="outline-none flex flex-col space-y-6 text-lg" variants={variants}>
      <div className="font-medium">Card Details</div>
      <div className="input-wrapper border-b border-grey-600 pt-1 pb-4 font-semibold">
        <div className="text-input  placeholder:font-medium">
          <CardElement hidePostalCode options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>
    </motion.div>
  )
}

const filterDates = ({ dates, treatment, availableTimes }) => {
  let filteredDates = dates

  if (treatment === "botox") {
    filteredDates = dates.filter((date) => {
      const day = dayjs(date).day()
      return day !== 0 && day !== 6
    })
  }

  // Filter out dates that have no available times after time filtering
  if (treatment === "hair") {
    filteredDates = dates.filter((date) => {
      const timesForDate = availableTimes[date]?.map((item) => ({ label: item.label, value: item.value }))
      const filteredTimes = filterTimes({ times: timesForDate, treatment })
      return filteredTimes && filteredTimes.length > 0
    })
  }

  return filteredDates
}

const filterTimes = ({ times, treatment }) => {
  if (treatment === "hair") {
    return times?.filter((time) => {
      const appointmentTime = dayjs(time.value).tz("Europe/Dublin")
      const hour = appointmentTime.hour()
      const day = appointmentTime.day()

      // Check if it's a weekend (Saturday = 6, Sunday = 0)
      const isWeekend = day === 0 || day === 6

      // On weekends: allow all times
      if (isWeekend) {
        return true
      }

      // On weekdays: only allow times before 4 PM
      return hour < 16
    })
  }
  return times
}

export const DateSelector = ({ label, disabled = false, availableTimes = {}, loadingAvailableTimes = false }) => {
  const { values, setFieldValue } = useFormikContext()

  const [{ value }, { error, touched }, { setValue }] = useField("date")
  const { variants } = useAnimation()

  const [maxDate, setMaxDate] = useState(null)
  const [filteredDates, setFilteredDates] = useState([])

  const minDate = new Date()
  const allDates = Object?.keys(availableTimes)

  useEffect(() => {
    if (availableTimes && Object.keys(availableTimes).length > 0) {
      setMaxDate(new Date(allDates[allDates.length - 1]))

      const filteredDates = filterDates({
        dates: allDates,
        treatment: values.treatment,
        availableTimes,
      })
      setFilteredDates(filteredDates)
    }
  }, [availableTimes, values.treatment, setFieldValue])

  return (
    <motion.div
      className={classNames("flex flex-col space-y-2 text-lg", loadingAvailableTimes && "cursor-not-allowed")}
      variants={variants}
    >
      <label className="font-medium text-lg">{label}</label>
      <div
        className={classNames(
          "relative flex justify-between gap-x-3 border-b border-grey-600 pt-1 pb-2",
          error && touched && "border-red-500 text-red-500",
          value === "" && "text-grey-400 font-medium",
          loadingAvailableTimes && "opacity-50 pointer-events-none cursor-not-allowed"
        )}
      >
        <DatePicker
          customInput={
            <div className={classNames("cursor-pointer", error && touched && "text-red-500")}>
              {loadingAvailableTimes ? (
                <span>
                  Getting available times
                  <span className="inline-flex">
                    <span className="animate-[fadeIn_1s_infinite_200ms]">.</span>
                    <span className="animate-[fadeIn_1s_infinite_400ms]">.</span>
                    <span className="animate-[fadeIn_1s_infinite_600ms]">.</span>
                  </span>
                </span>
              ) : value !== "" ? (
                moment(value).format("dddd LL")
              ) : (
                "Select Date"
              )}
            </div>
          }
          selected={value ? new Date(value) : null}
          onChange={(date) => setValue(moment(date).format("YYYY-MM-DD"))}
          // dateFormat="dd/MM/yyyy"
          filterDate={(date) => filteredDates.includes(moment(date).format("YYYY-MM-DD"))}
          calendarStartDay={1}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
        />
        <ErrorMessage name="date" component="span" className={classNames("text-red-500 font-semibold", "text-sm self-center")} />
      </div>
    </motion.div>
  )
}

const TimeSelector = ({ availableTimes }) => {
  const { setFieldValue, values } = useFormikContext()
  const [, , { setValue }] = useField("time")
  const [{ value: dateValue }] = useField("date")

  const times = availableTimes?.[dateValue]?.map((item) => ({ label: item.label, value: item.value }))
  const filteredTimes = filterTimes({ times, treatment: values.treatment })

  useEffect(() => {
    setFieldValue("time", "")
  }, [dateValue])

  return (
    <Select
      name="time"
      label="Select Appointment Time"
      placeholder={"Select Time"}
      options={filteredTimes || []}
      disabled={false}
      onChange={(value) => setValue(value)}
    />
  )
}

const OnlineConsultationCheck = ({ hasOnlineConsultation }) => {
  const [{ value: treatmentValue }] = useField("treatment")

  if (treatmentValue === "hair" && !hasOnlineConsultation) {
    return (
      <Checkbox
        label="Online Consultation Confirmation"
        name="onlineConsultationConfirm"
        text={
          <>
            I confirm that I have completed an{" "}
            <Link href="/consultation?type=online">
              <a className="underline text-blue">Online Consultation</a>
            </Link>{" "}
            and I have received a response to make an appointment. <br />
            <span className="text-sm">
              I also agree to the{" "}
              <Link href="/forms/terms-and-conditions">
                <a className="underline text-blue" target="_blank" rel="noopener noreferrer">
                  Terms & Conditions
                </a>
              </Link>
            </span>
          </>
        }
        helperText="Please note that your €50 deposit may not be refundable if this is ignored"
      />
    )
  } else {
    return null
  }
}

const ErrorScroller = () => {
  const { errors, submitCount } = useFormikContext()

  useEffect(() => {
    if (Object.keys(errors).length > 0 && submitCount > 0) {
      const firstErrorField = Object.keys(errors)[0]
      const element = document.querySelector(`[name="${firstErrorField}"]`)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }, [submitCount, errors])

  return null
}

const InPersonConsultationForm = ({ description, successMessage }) => {
  const stripe = useStripe()
  const elements = useElements()

  const { query } = useRouter()
  const hasOnlineConsultation = query?.["online-confirmed"] === "true"

  const { animation } = useAnimation("container", 0.1)

  const [availableTimes, setAvailableTimes] = useState()
  const [loadingAvailableTimes, setLoadingAvailableTimes] = useState(false)

  const getAvailableTimes = async () => {
    setLoadingAvailableTimes(true)
    const availableTimes = await axios.get(
      `${API}/api/cliniko/available-times/${process.env.NEXT_PUBLIC_CLINIKO_APPOINTMENT_TYPE_INITIAL_CONSULTATION_ID}`
    )

    setAvailableTimes(availableTimes?.data)
    setLoadingAvailableTimes(false)
  }

  useEffect(() => {
    getAvailableTimes()
  }, [])

  const [formComplete, setFormComplete] = useState(false)

  const validationSchema = Yup.object().shape({
    treatment: Yup.string().required("Required"),
    name: Yup.string()
      .required("Required")
      .matches(/^[\w\s]+ [\w\s]+$/, "Please Enter Your Full Name"),
    email: Yup.string().required("Required"),
    phone: Yup.string().required("Required"),
    date: Yup.string().required("Required"),
    time: Yup.string().required("Required"),
    onlineConsultationConfirm: Yup.boolean().when("treatment", {
      is: (treatment) => treatment === "hair" && !hasOnlineConsultation,
      then: () => Yup.boolean().oneOf([true], "Required"),
    }),
  })

  return (
    <motion.div className="space-y-8" {...animation}>
      <WysiwygText text={description} animate />
      <Form
        key="1"
        initialValues={{
          treatment: query?.treatment || "",
          name: query?.name || "",
          email: query?.email || "",
          phone: query?.phone || "",
          date: "", //Next available
          time: "",
          onlineConsultationConfirm: false,
        }}
        validationSchema={validationSchema}
        submitText="Confirm and Pay"
        submittingText="Confirming"
        formComplete={formComplete}
        successMessage={successMessage}
        onSubmit={async (values, { setSubmitting, setStatus, resetForm }) => {
          setStatus()
          setSubmitting(true)
          if (!stripe || !elements) {
            setStatus("Payment form is still loading. Please wait a moment and try again.")
            setSubmitting(false)
            return
          }

          const nameParts = values.name.split(" ")
          const firstName = nameParts?.[0]
          const lastName = nameParts?.slice(1).join(" ")

          let patientId
          let customerId

          try {
            // 1. First find or create the patient/customer
            const valuesToSubmit = {
              data: {
                ...values,
                phone: `+${values.phone}`,
                first_name: firstName,
                last_name: lastName,
                patient_phone_numbers: [
                  {
                    number: `+${values.phone}`,
                    normalized_number: `+${values.phone.toString().replace(/\s+/g, "")}`,
                    phone_type: "mobile",
                  },
                ],
                patient_type: values.treatment === "botox" ? "botox" : "hair",
                date: moment(values.date).format("YYYY-MM-DD"),
                time: moment(values.time).format("HH:mm:ss"),
                formattedDate: `${dayjs(values.time).tz("Europe/Dublin").format("HH:mm")} on ${dayjs(values.date).format(
                  "dddd, Do MMMM YYYY"
                )}`,
              },
            }

            const patientResponse = await axios.post(`${API}/api/cliniko/patients/find-or-create`, valuesToSubmit)
            const patient = patientResponse.data
            patientId = patient?.id
            customerId = getStripeCustomerId(patient)

            if (!customerId) {
              throw new Error("Stripe customer ID is required")
            }

            // 2. Process the payment with the customer ID
            const { data: paymentIntent } = await axios.post(`${API}/api/stripe/payment-intent/${customerId}`, {
              amount: 5000,
              description: values.treatment === "botox" ? "Booking Deposit (Botox)" : "Consultation Fee",
            })

            if (paymentIntent?.error) {
              throw new Error(paymentIntent.error.message)
            }

            if (!paymentIntent?.client_secret) {
              throw new Error("Missing payment client_secret")
            }

            customerId = paymentIntent.customerId || customerId

            const result = await stripe.confirmCardPayment(paymentIntent.client_secret, {
              payment_method: {
                card: elements.getElement(CardElement),
                billing_details: {
                  name: values.name,
                  email: values.email,
                  phone: `+${values.phone}`,
                },
              },
              receipt_email: values.email,
              shipping: {
                name: values.name,
                tracking_number: `Appointment: ${values.date} ${values.time}`,
                address: {
                  line1: "",
                  line2: "",
                  city: "",
                  state: "",
                  postal_code: "",
                },
              },
            })

            if (result.error) {
              throw new Error(result.error.message)
            }

            if (result.paymentIntent.status === "succeeded") {
              // 3. Create the appointment
              await axios.post(`${API}/api/cliniko/appointments`, {
                appointmentTypeId:
                  values.treatment === "botox"
                    ? process.env.NEXT_PUBLIC_CLINIKO_APPOINTMENT_TYPE_BOTOX_ID
                    : process.env.NEXT_PUBLIC_CLINIKO_APPOINTMENT_TYPE_INITIAL_CONSULTATION_ID,
                patientId: patient?.id,
                startsAt: values.time,
                endsAt: dayjs(values.time).add(15, "minute").toISOString(),
              })

              // 4. Update credit balance
              await axios.post(`${API}/api/stripe/credit-balance/${customerId}`, {
                amount: -5000,
                description: values.treatment === "botox" ? "Booking Deposit (Botox)" : "Consultation Fee",
              })

              // // 5. Save the consultation form
              try {
                await axios.post(`${API}/api/form-in-person-consultations`, {
                  ...valuesToSubmit,
                  data: {
                    ...valuesToSubmit.data,
                    payment: result,
                    patientId: patient?.id,
                  },
                })
              } catch (error) {
                console.error("❌ Form save failed:", error?.response?.data || error?.message)
                console.warn("⚠️ Form save failed but appointment was created successfully")
              }

              setFormComplete(true)
              resetForm()
              trackConsultationSubmit({
                type: "in_clinic",
                treatment: mapInClinicTreatment(values.treatment),
              })
            }
          } catch (error) {
            Sentry.captureException(error, {
              extra: {
                formValues: values,
                component: "InPersonConsultationForm",
                action: "form submission",
                patientId,
                hasCustomerId: Boolean(customerId),
                paymentIntentErrorType: error?.response?.data?.error?.type,
              },
            })

            setStatus(getPaymentErrorMessage(error))
          } finally {
            setSubmitting(false)
          }
        }}
      >
        <ErrorScroller />
        <Select
          name="treatment"
          label="Appointment Type"
          placeholder="Select Appointment Type"
          options={[
            { label: "Hair Consultation", value: "hair" },
            { label: "Anti Wrinkle Injections", value: "botox" },
          ]}
        />
        <Field name="name" label="Name" placeholder="Enter your full name" />
        <Field name="email" label="Email" placeholder="Enter your email" type="email" />
        <PhoneNumberField />
        <div className="grid xl:grid-cols-2 gap-8 xl:gap-4">
          <DateSelector label="Select Appointment Date" availableTimes={availableTimes} loadingAvailableTimes={loadingAvailableTimes} />
          <TimeSelector availableTimes={availableTimes} />
        </div>

        <OnlineConsultationCheck hasOnlineConsultation={hasOnlineConsultation} />

        <CardField />
      </Form>
    </motion.div>
  )
}

export default InPersonConsultationForm
